import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { filesApi } from '@/api/http-client';

export interface UseDeleteFileOptions {
  ownerId: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export interface UseDeleteFileResult {
  deleteFile: (fileId: string) => Promise<void>;
  isDeleting: boolean;
  error: Error | null;
}

export function useDeleteFile(options: UseDeleteFileOptions): UseDeleteFileResult {
  const { ownerId, onSuccess, onError } = options;
  const queryClient = useQueryClient();
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteFile = async (fileId: string): Promise<void> => {
    setIsDeleting(true);
    setError(null);

    try {
      await filesApi.deleteFile(fileId);
      setIsDeleting(false);
      onSuccess?.();
      queryClient.invalidateQueries({ queryKey: ['files', ownerId] });
    } catch (err) {
      const deleteError = err instanceof Error ? err : new Error('Delete failed');
      setError(deleteError);
      setIsDeleting(false);
      onError?.(deleteError);
      throw deleteError;
    }
  };

  return {
    deleteFile,
    isDeleting,
    error,
  };
}
